import { api, ApiError } from './apiClient';
import { logger } from '../utils/logger';

export interface SubscriptionStatusResponse {
  tier: 'free' | 'trial' | 'premium' | 'expired_trial';
  isPremium: boolean;
  trialActive: boolean;
  trialDaysRemaining: number;
  trialStartDate: string | null;
  trialEndDate: string | null;
  usage: {
    scanCount: number;
    recipeCount: number;
    manualCount: number;
  };
  limits: {
    dailyScans: number;
    dailyRecipes: number;
    dailyManual: number;
  };
}

export interface UsageIncrementResponse {
  allowed: boolean;
  remaining: number;
  limit?: number;
}

/**
 * Fetch current subscription status + today's usage from the server.
 */
export async function fetchSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
  const res = await api.get<{ data: SubscriptionStatusResponse }>('/subscriptions/status');
  return res.data;
}

/**
 * Start a 7-day trial.
 */
export async function startTrial(): Promise<SubscriptionStatusResponse> {
  const res = await api.post<{ data: SubscriptionStatusResponse }>('/subscriptions/start-trial');
  return res.data;
}

/**
 * Increment a usage counter on the server. Returns whether the action is allowed.
 */
export async function incrementUsage(type: 'scan' | 'recipe' | 'manual'): Promise<UsageIncrementResponse> {
  const res = await api.post<{ data: UsageIncrementResponse }>('/usage/increment', { type });
  return res.data;
}

/**
 * Check if the user can perform the action (without incrementing).
 * Denies by default on network error — server is source of truth for limits.
 */
export async function checkUsageAllowed(type: 'scan' | 'recipe' | 'manual'): Promise<boolean> {
  try {
    const status = await fetchSubscriptionStatus();
    if (status.isPremium) return true;
    const usage = status.usage;
    const limits = status.limits;
    switch (type) {
      case 'scan': return usage.scanCount < limits.dailyScans;
      case 'recipe': return usage.recipeCount < limits.dailyRecipes;
      case 'manual': return usage.manualCount < limits.dailyManual;
    }
  } catch (err) {
    logger.warn('Failed to check usage against server, denying by default:', err);
    return false;
  }
}
